/*************************************************************
 * RootCauseAnalysisTab.js - Root cause analysis tab
 *************************************************************/

import { useState, useEffect, useCallback } from 'react';
import { useAppContext } from '../context/AppContext.jsx';
import { api } from '../api/reportingApi.js';
import { LoadingSpinner } from '../components/ui/LoadingSpinner.jsx';
import { ErrorBoundary } from '../components/ui/ErrorBoundary.jsx';

export const RootCauseAnalysisTab = () => {
    const { state, setLoading, setError, addNotification } = useAppContext();
    const [analysisData, setAnalysisData] = useState(null);

    const loadAnalysis = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const data = await api.getRootCauseAnalysis(state.filters);
            setAnalysisData(data);

            addNotification({
                type: 'success',
                message: 'Root cause analysis completed',
                duration: 3000
            });
        } catch (error) {
            console.error('[RootCause] Error loading:', error);
            setError(error);
            addNotification({
                type: 'danger',
                message: 'Failed to perform root cause analysis: ' + error.message
            });
        } finally {
            setLoading(false);
        }
    }, [state.filters, setLoading, setError, addNotification]);

    useEffect(() => {
        loadAnalysis();
    }, [loadAnalysis]);

    if (state.loading) {
        return (
            <LoadingSpinner
                message="Analyzing root causes..."
                size="large"
            />
        );
    }

    return (
        <ErrorBoundary error={state.error} onRetry={loadAnalysis}>
            <div className="root-cause-content fade-in">
                {analysisData && (
                    <div>
                        <div className="row mb-4">
                            <div className="col-12">
                                <div className="card shadow-sm">
                                    <div className="card-header bg-primary text-white">
                                        <h5 className="mb-0">
                                            <i className="fas fa-search-plus mr-2"></i>
                                            Key Insights
                                        </h5>
                                    </div>
                                    <div className="card-body">
                                        {analysisData.insights && analysisData.insights.length > 0 ? (
                                            analysisData.insights.map((insight, index) => (
                                                <div
                                                    key={index}
                                                    className={`alert alert-${
                                                        insight.severity === 'high' ? 'danger' :
                                                        insight.severity === 'medium' ? 'warning' : 'info'
                                                    } mb-3`}
                                                >
                                                    <h6 className="alert-heading">
                                                        <i className={`fas fa-${
                                                            insight.type === 'temporal' ? 'clock' :
                                                            insight.type === 'station' ? 'server' :
                                                            insight.type === 'model_performance' ? 'brain' : 'info-circle'
                                                        } mr-2`}></i>
                                                        {insight.category?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                    </h6>
                                                    <p className="mb-0">{insight.insight}</p>
                                                    <small className="text-muted">
                                                        Confidence: {(insight.confidence * 100).toFixed(0)}%
                                                    </small>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-muted">
                                                No significant insights found for the selected period
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="row mb-4">
                            <div className="col-md-6">
                                <div className="card shadow-sm h-100">
                                    <div className="card-header">
                                        <h5 className="mb-0">
                                            <i className="fas fa-server text-primary mr-2"></i>
                                            Station Analysis
                                        </h5>
                                    </div>
                                    <div className="card-body">
                                        {analysisData.station_patterns?.highest_defect_stations && (
                                            <div>
                                                <h6>Problematic Stations</h6>
                                                <ul className="list-unstyled">
                                                    {analysisData.station_patterns.highest_defect_stations.map((station, idx) => (
                                                        <li key={idx}>
                                                            {station._id || 'Unknown'} - {station.defect_count} defects
                                                            <small className="d-block text-muted">
                                                                Avg Confidence: {(station.avg_confidence * 100).toFixed(1)}%
                                                            </small>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-12">
                                <div className="card shadow-sm">
                                    <div className="card-header bg-success text-white">
                                        <h5 className="mb-0">
                                            <i className="fas fa-lightbulb mr-2"></i>
                                            Recommendations
                                        </h5>
                                    </div>
                                    <div className="card-body">
                                        {analysisData.recommendations && analysisData.recommendations.length > 0 ? (
                                            <div className="list-group">
                                                {analysisData.recommendations.map((rec, index) => (
                                                    <div key={index} className="list-group-item">
                                                        <div className="d-flex justify-content-between align-items-start">
                                                            <div>
                                                                <h6 className="mb-1">{rec.action}</h6>
                                                                <p className="mb-1 text-muted">
                                                                    Timeline: {rec.timeline.replace(/_/g, ' ')}
                                                                </p>
                                                                <small>
                                                                    Resource: {rec.resource_required.replace(/_/g, ' ')}
                                                                </small>
                                                            </div>
                                                            <span className={`badge badge-${
                                                                rec.priority === 'high' ? 'danger' :
                                                                rec.priority === 'medium' ? 'warning' : 'info'
                                                            }`}>
                                                                {rec.priority}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-muted mb-0">
                                                No specific recommendations at this time
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </ErrorBoundary>
    );
};